/**
 * Inline-SVG chart geometry primitives for the admin metrics dashboard.
 *
 * Mirrors the dashboard's `buildTrafficGeometry` shape (apps/dashboard
 * `views/dashboard/Dashboard.tsx`) — no chart library; per CLAUDE.md §8
 * we don't add dependencies casually, and recharts/chartjs would be a
 * second SVG runtime alongside the existing inline-SVG idiom.
 *
 * Defensive against the same three real-data edge cases the dashboard
 * defends:
 *   - **Empty series** — `Math.max(...[])` returns `-Infinity` →
 *     `yAt` would yield `NaN`. We clamp `maxY` ≥ 1 so a fresh deploy
 *     with no rollup buckets still renders a flat baseline instead of
 *     an unrendered `<path d="NaN…">`.
 *   - **All-zero series** — same clamp covers `maxY === 0`.
 *   - **Single-point series** — `xAt = (i / (data.length - 1)) * W`
 *     divides by zero when `data.length === 1`. We center the point
 *     at `x = W / 2` so a 24h-with-single-bucket window still has a
 *     visible mark.
 *
 * Pure functions; exported for tests. The geometry returns
 * `lineFor` / `areaFor` builders parameterised by a value-accessor so a
 * single helper drives every series (new accounts, mandates,
 * transactions, etc.).
 */

export const CHART_W = 600;
export const CHART_H = 200;

export interface SeriesGeometry<T> {
  readonly width: number;
  readonly height: number;
  readonly maxY: number;
  readonly xAt: (i: number) => number;
  readonly yAt: (v: number) => number;
  readonly lineFor: (accessor: (point: T) => number) => string;
  readonly areaFor: (accessor: (point: T) => number) => string;
}

export function buildSeriesGeometry<T>(
  data: ReadonlyArray<T>,
  accessors: ReadonlyArray<(point: T) => number>,
): SeriesGeometry<T> {
  const W = CHART_W;
  const H = CHART_H;
  // Take the max across every accessor — so a multi-series chart
  // (e.g. mandates_evaluated + mandates_approved overlay) shares one
  // y-scale and the visual comparison is honest.
  let maxY = 1;
  for (const point of data) {
    for (const get of accessors) {
      const v = get(point);
      if (Number.isFinite(v) && v > maxY) maxY = v;
    }
  }
  const xAt = (i: number): number => {
    if (data.length <= 1) return W / 2;
    return (i / (data.length - 1)) * W;
  };
  const yAt = (v: number): number => {
    if (!Number.isFinite(v)) return H;
    return H - (v / maxY) * H;
  };
  const lineFor = (accessor: (point: T) => number): string => {
    if (data.length === 0) return `M 0 ${H} L ${W} ${H}`;
    if (data.length === 1) {
      const p = data[0] as T;
      const y = yAt(accessor(p));
      return `M 0 ${y.toFixed(1)} L ${W} ${y.toFixed(1)}`;
    }
    return data
      .map(
        (p, i) =>
          `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(accessor(p)).toFixed(1)}`,
      )
      .join(' ');
  };
  const areaFor = (accessor: (point: T) => number): string =>
    `${lineFor(accessor)} L ${W} ${H} L 0 ${H} Z`;
  return { width: W, height: H, maxY, xAt, yAt, lineFor, areaFor };
}

/**
 * Pick at most `target` evenly-spaced labels from a longer label list,
 * preserving array length (other slots are empty strings) so the
 * surrounding `flex justify-between` keeps one slot per bucket.
 *
 * Same shape as the dashboard's `thinAxisLabels`. The first + last
 * labels are always kept so the time-range boundaries are anchored.
 */
export function thinAxisLabels(labels: ReadonlyArray<string>, target = 7): string[] {
  const n = labels.length;
  if (n <= target) return labels.slice();
  const stride = Math.ceil((n - 1) / (target - 1));
  const out = labels.map(() => '');
  for (let i = 0; i < n; i += stride) {
    out[i] = labels[i] ?? '';
  }
  out[n - 1] = labels[n - 1] ?? '';
  return out;
}

/**
 * Build evenly-spaced y-axis gridline values from 0 to `maxY`.
 * Returns `count + 1` values (0 included). For a clean axis we round
 * `maxY` upward to a "nice" number (1/2/2.5/5/10 family).
 */
export function niceGridlines(maxY: number, count = 4): number[] {
  if (!Number.isFinite(maxY) || maxY <= 0) {
    const step = 1 / count;
    return Array.from({ length: count + 1 }, (_, i) => i * step);
  }
  const nice = niceCeil(maxY);
  const step = nice / count;
  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const exp = Math.floor(Math.log10(v));
  const base = Math.pow(10, exp);
  const norm = v / base;
  let nice: number;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 2.5) nice = 2.5;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

/**
 * Sum a finite array. Defends against `NaN` slipping in.
 */
export function sumFinite(xs: ReadonlyArray<number>): number {
  let total = 0;
  for (const x of xs) if (Number.isFinite(x)) total += x;
  return total;
}
